///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { animation, easing } from "@stovokor/math/animation"
import { matrix } from "@stovokor/math/matrix"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Marker radius scales with the square root of acreage so area reads
// linearly: 1k acres ~ 1.6 km, 100k acres ~ 16 km, capped at 25 km
const cMinRadius = 1.5
const cMaxRadius = 25

// Screen-space size floors (radians), mirroring the quake marker tiers:
// active fires read as a clear dot from orbit, contained fires stay subtle
const cMinAngularActive = 0.003
const cMinAngularContained = 0.0009

// Perimeter updated within this window counts as "breaking" and pulses
const cBreakingHours = 24

// Face-on solidity of the dome so the marker reads over bright terrain
const cFireFill = 0.5

export interface WildfireMarkerStyle {
	acres: number | null
	active: boolean
	dateCurrentMs: number
}

/** Dome radius in km for a given fire acreage */
export function wildfireMarkerRadius(acres: number | null): number {
	const radius = Math.sqrt(Math.max(acres ?? 0, 0)) / 20
	return Math.min(cMaxRadius, Math.max(cMinRadius, radius))
}

/**
 * Centroid marker for a wildfire: an ember dome in flame orange-red sized
 * to the fire's acreage. Pulses only while the fire is active and its
 * perimeter was updated in the last day; contained fires are dim and static.
 */
export class WildfireMarkerNode extends NodeBase {
	private readonly dome: EmberDomeNode
	private focusAnim?: animation
	private focusBlend = 0
	private focused = false

	public radius: number
	/** Current minimum apparent size (radians); used for hit test inflation too */
	public minAngularSize = 0

	constructor(context: StovokorContext, style: WildfireMarkerStyle) {
		super()
		this.radius = wildfireMarkerRadius(style.acres)

		this.dome = new EmberDomeNode(context)
		this.dome.fillAlpha = cFireFill
		this.addNode(this.dome)

		this.updateStyle(style)
	}

	/** Recompute size, color and animation state. Called on creation and on
	 *  every dataset poll, which is what decays the pulse over time. */
	public updateStyle(style: WildfireMarkerStyle): void {
		this.radius = wildfireMarkerRadius(style.acres)
		this.dome.transform = matrix.scale(this.radius)

		const ageHours = (Date.now() - style.dateCurrentMs) / 3.6e6
		const breaking = style.active && ageHours < cBreakingHours

		this.dome.color = DesignColors.MARKER_WILDFIRE
		this.dome.pulse = breaking ? 1 : 0
		this.dome.opacity = (style.active ? 1 : 0.55) * (1 - 0.75 * this.focusBlend)

		this.minAngularSize = style.active ? cMinAngularActive : cMinAngularContained
		this.dome.minAngularSize = this.minAngularSize
	}

	/** Fade the marker down while focused so the perimeter takes over visually */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) {
			return
		}
		this.focused = focused

		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(focused ? 1 : 0)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => {
				this.focusBlend = value
				this.dome.opacity = 1 - 0.75 * value
			})
		this.focusAnim.start()
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}
}
